import { HtmlContent } from '@/components/UI/HtmlContent'
import { Image } from '@/components/UI/Image'
import type { StampInRewardData } from '@/schemas/v2'
import { type ReactNode, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { type AcquisitionStatus, RewardAcquisitionStatusBadge } from '../RewardAcquisitionStatusBadge'
import { RewardDetailTabs, type TabItem } from './RewardDetailTabs'

type Props = {
  /** ヒーロー画像URL */
  image?: string | null
  /** 特典名 */
  name: string
  /**
   * 獲得ステータス。バッジ表示に使用する。
   * null / undefined の場合はバッジ非表示（showLotteryDoneLabel が true なら表示）。
   */
  status?: AcquisitionStatus | null
  /**
   * true のとき、status に関わらず「終了」（グレー）バッジを強制表示する。
   * ACHIEVED + 抽選型 + prizes 全ハズレ、または LOST の場合に使用する。
   */
  showLotteryDoneLabel?: boolean
  /**
   * 獲得状況テキスト（例: ["スタンプ獲得状況 1/6", "ポイント 200/500"]）。
   * 空配列または未指定の場合は非表示。
   */
  progressTexts?: string[]
  /** 特典の説明文。未指定の場合は非表示。 */
  description?: string | null
  /**
   * 「獲得条件を確認」ボタンのコールバック。
   * 未指定の場合はボタン非表示。
   */
  onCheckCondition?: () => void
  /**
   * 複数特典タブのデータ。2件以上でタブを表示、1件以下は非表示。
   */
  tabs?: TabItem[]
  /** アクティブなタブのインデックス */
  activeTabIndex?: number
  /** タブ切り替えコールバック */
  onTabChange?: (index: number) => void
  /**
   * 特典グループ名。画像とタブの間に表示する。
   * 未指定の場合は非表示。
   */
  groupName?: string | null
  /**
   * 獲得条件に関連するスタンプ一覧。
   * 空配列の場合はセクション非表示。
   * バックエンドが stamps を返し始めるまでは空配列が渡る想定。
   */
  stamps?: StampInRewardData[]
  /**
   * アクションボタン領域。
   * PresentType*Components や RewardButtonWithState 等を渡す。
   */
  children?: ReactNode
}

/**
 * `Raund-Replace / Screen UI / REWORD`（node: 188:1327）の特典詳細コンテンツ。
 * ヘッダー・フッターは含まない。
 *
 * @example
 * <RewardDetailContent
 *   image="/images/reward.jpg"
 *   name="特典名を表示"
 *   status="UNOBTAINED"
 *   progressTexts={['スタンプ獲得状況 1/6']}
 *   description="特典の説明文..."
 *   onCheckCondition={() => openConditionDialog()}
 *   tabs={[{ id: 1, label: '特典 1' }, { id: 2, label: '特典 2' }]}
 *   activeTabIndex={0}
 *   onTabChange={setActiveTabIndex}
 * >
 *   <PresentType01DownloadComponents {...props} />
 * </RewardDetailContent>
 */
export const RewardDetailContent = memo(
  ({
    image,
    name,
    status,
    showLotteryDoneLabel,
    progressTexts,
    description,
    onCheckCondition,
    tabs,
    activeTabIndex = 0,
    onTabChange,
    groupName,
    stamps,
    children,
  }: Props) => {
    const { t } = useTranslation()

    return (
      <div className="flex flex-col">
        {/* ヒーロー画像 */}
        <div className="h-[284px] w-full overflow-hidden bg-[#d9d9d9]">
          {image && <Image src={image} alt={name} fit="contain-natural" />}
        </div>

        {/* 特典グループ名 */}
        {groupName && (
          <p className="px-[16px] pt-[16px] text-[16px] font-bold leading-[24px] text-black">{groupName}</p>
        )}

        {/* タブ（複数特典がある場合のみ表示） */}
        {tabs && <RewardDetailTabs tabs={tabs} activeIndex={activeTabIndex} onChange={onTabChange ?? (() => {})} />}

        {/* コンテンツエリア */}
        <div className="px-[16px] pt-[16px] pb-[32px]">
          {/* 特典名 */}
          <h1 className="text-[16px] font-bold leading-[24px] text-black">{name}</h1>

          {/* バッジ行 + 獲得条件ボタン */}
          <div className="mt-[8px] flex items-center justify-between">
            {/* 状態バッジ */}
            <RewardAcquisitionStatusBadge status={status} showLotteryDoneLabel={showLotteryDoneLabel} />

            {/* 獲得条件を確認ボタン */}
            {onCheckCondition && (
              <button
                type="button"
                onClick={onCheckCondition}
                className="flex h-[32px] w-[123px] items-center justify-center rounded-[8px] border border-main bg-white text-[13px] font-bold leading-[16px] text-main"
              >
                {t('reward_detail.check_condition', '獲得条件を確認')}
              </button>
            )}
          </div>

          {/* 獲得状況テキスト */}
          {progressTexts && progressTexts.length > 0 && (
            <div className="mt-[8px] flex flex-col gap-[4px]">
              {progressTexts.map((text, i) => (
                <p key={i} className="text-[13px] font-bold leading-[20px] text-black">
                  {text}
                </p>
              ))}
            </div>
          )}

          {/* 説明文 */}
          {description && <HtmlContent html={description} variant="body2" className="mt-[40px]" />}

          {/* スタンプ一覧 */}
          {stamps && stamps.length > 0 && (
            <div className="mt-[24px]">
              <p className="text-[13px] font-bold leading-[20px] text-black">
                {t('reward_detail.required_stamps', '獲得条件スタンプ')}
              </p>
              <ul className="mt-[8px] flex flex-col gap-[8px]">
                {stamps.map((stamp) => (
                  <li key={stamp.id} className="flex items-center gap-[8px]">
                    <div className="h-[40px] w-[40px] shrink-0 overflow-hidden rounded-full bg-[#d9d9d9]">
                      {stamp.image && <img src={stamp.image} alt={stamp.name} className="h-full w-full object-cover" />}
                    </div>
                    <span className="text-[13px] leading-[20px] text-black">{stamp.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* アクションボタン領域 */}
          {children && <div className="mt-[40px] flex flex-col items-stretch gap-0">{children}</div>}
        </div>
      </div>
    )
  },
)
